import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, notification, Typography, Flex } from 'antd';
import { useAuthService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { notificationType } from '../../common/constants/notificationType';
import { routesPath } from '../../common/constants/routesPath';
import classes from './TwoFactorAuth.module.scss';
import TwoFactorAuthForm from '../../components/auth/TwoFactorAuthForm.js';
import { useTranslation } from 'react-i18next';
import PageContainer from './../../common/components/PageContainer';
import { useMediaQuery } from '@uidotdev/usehooks';
import { primitiveColors, spacing } from './../../theming';
import globalNotification from '../../common/hooks/globalNotification';
import { apiErrorText } from '../../utils/apiErrorText';

// the code step cannot succeed any more: the password step's token expired or 2FA was switched off meanwhile
const mustSignInAgain = error =>
    error?.response?.status === 401 || error?.response?.data?.code === 'TWO_FACTOR_AUTH_NOT_ENABLED';

export default function TwoFactorAuth() {
    const [api, contextHolder] = notification.useNotification();
    const navigate = useNavigate();
    const { t } = useTranslation(['settings', 'apiResponses']);
    const mobileDevicesBreakpoint = useMediaQuery('only screen and (max-width : 840px)');

    const { twoFactorAuth } = useAuthService();
    const { setAuth } = useAuth();
    const [loading, setLoading] = useState(false);

    const openNotification = ({ type = 'error', message = '', description = '', placement = 'bottomRight' }) => {
        api[type]({
            message: message,
            description: description,
            placement: placement,
        });
    };

    const onBackClick = () => {
        setAuth(null, null);
        navigate(routesPath.AUTH.PATH);
    };

    const onFormSubmit = async form => {
        try {
            setLoading(true);
            const twoFactorTokens = await twoFactorAuth(form.code);

            if (twoFactorTokens?.data) {
                const { accessToken, refreshToken } = twoFactorTokens.data;
                setAuth(accessToken, refreshToken);
                // same next step as a password sign-in without 2FA (SignInForm): pick a team
                navigate(routesPath.ROOT.CREATE_TEAM_AUTH);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (mustSignInAgain(error)) {
                setAuth(null, null);
                // the page unmounts on the way out, so the notice goes through the app-wide notifications
                globalNotification.open({
                    type: notificationType.WARNING,
                    message: t('apiResponses:SIGN_IN_AGAIN.message'),
                    description: t('apiResponses:SIGN_IN_AGAIN.description'),
                });
                navigate(`${routesPath.AUTH.PATH}/${routesPath.AUTH.SIGN_IN}`);
                return;
            }
            openNotification({ type: notificationType.ERROR, ...apiErrorText(t, error) });
        }
    };

    return (
        <PageContainer
            style={{
                backgroundColor: mobileDevicesBreakpoint ? 'transparent' : primitiveColors.gray0,
                padding: mobileDevicesBreakpoint ? spacing[1] : spacing[4],
            }}
        >
            {contextHolder}
            <Flex vertical align="center" justify="center">
                <Card title={t('settings:2FA-title')} bordered={false} style={{ width: 'clamp(280px, 85vw, 470px)' }}>
                    <div className={classes.content}>
                        <Typography.Text>{t('settings:2FA.page.description')}</Typography.Text>
                        <TwoFactorAuthForm
                            loading={loading}
                            showCancel={true}
                            submitDisabled={false}
                            onBackClick={onBackClick}
                            onFormSubmit={onFormSubmit}
                            t={t}
                        />
                    </div>
                </Card>
            </Flex>
        </PageContainer>
    );
}
